import { useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteTexts } from "@/lib/siteConfig";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function EditableText({ slot, defaultText = "", className = "", multiline = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { texts, setText } = useSiteTexts();
  const value = texts[slot] != null && texts[slot] !== "" ? texts[slot] : defaultText;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  if (!isAdmin) return <span className={className}>{value}</span>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/text", { slot, value: draft });
      setText(slot, draft);
      setEditing(false);
      toast.success("Testo aggiornato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-start gap-1.5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        {multiline ? (
          <textarea
            data-testid={`edit-text-input-${slot}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="bg-[#0B1220] border border-gold/40 rounded px-2 py-1 text-sm text-foreground min-w-[320px] w-[420px] max-w-[80vw] align-top"
          />
        ) : (
          <input
            data-testid={`edit-text-input-${slot}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="bg-[#0B1220] border border-gold/40 rounded px-2 py-1 text-sm text-foreground min-w-[180px]"
          />
        )}
        <button data-testid={`edit-text-save-${slot}`} onClick={save} disabled={saving} className="text-gold hover:text-[#FDE047] mt-1">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        <button data-testid={`edit-text-cancel-${slot}`} onClick={() => { setDraft(value); setEditing(false); }} className="text-muted-foreground hover:text-destructive mt-1">
          <X className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 group/txt ${className}`}>
      {value}
      <button
        data-testid={`edit-text-btn-${slot}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDraft(value); setEditing(true); }}
        className="opacity-0 group-hover/txt:opacity-100 transition-opacity text-muted-foreground hover:text-gold"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </span>
  );
}
